import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Snackbar, IconButton, Alert, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../../components/DataGrid/ControlledDataGrid';
import { CSVDownloader } from "react-papaparse";
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

const DEFAULT_GRID_ID = 43;
const REPORT_NAME = "EVV Report";

const DEFAULT_DATE_RANGE = [dayjs().startOf("month"), dayjs().endOf("month")];

const formatAddress = (loc) =>
  [loc?.loc_addr_line1, loc?.loc_addr_line2, loc?.loc_city, loc?.loc_state, loc?.loc_zip]
    .filter(Boolean)
    .join(", ");

// Grid cells that can hold long text wrap instead of being clipped.
const renderWrapped = (params) => (
  <div>
    <p className="text-wrap" style={{ lineHeight: "1", marginTop: "12px" }}>
      {params.value}{" "}
    </p>
  </div>
);

// csv_header is the label used for the CSV/print exports; headerName is the on-screen label.
// csvValue converts a row value that is not already a string for those exports.
const columns = [
  {
    field: "Provider",
    headerName: "Service Provider",
    flex: 1,
    renderCell: renderWrapped,
    csv_header: "Service Provider",
  },
  {
    field: "Receiver",
    headerName: "Service Receiver",
    flex: 1,
    renderCell: renderWrapped,
    csv_header: "Service Receiver",
  },
  {
    field: "TaskType",
    headerName: "Type",
    flex: 1,
    csv_header: "Type of Service",
  },
  {
    field: "DateofService",
    headerName: "Date",
    flex: 1,
    csv_header: "Date of the Service",
  },
  {
    field: "StarttoEnd",
    headerName: "Time",
    flex: 1,
    renderCell: renderWrapped,
    csv_header: "Time the service starts and ends",
  },
  {
    field: "Location",
    headerName: "Location",
    flex: 2,
    sortable: false,
    renderCell: (params) => {
      const addresses = (params.value ?? []).map(formatAddress).filter(Boolean);
      if (addresses.length === 0) return <div>-</div>;
      return (
        <div>
          {addresses.map((address, index) => (
            <div key={index}>{address}</div>
          ))}
        </div>
      );
    },
    csv_header: "Location of the Service",
    csvValue: (value) => (value ?? []).map(formatAddress).filter(Boolean).join(" | ") || "-",
  },
];

const buildRow = (timesheet) => {
  const { empDets, taskDets, locDets } = timesheet;
  const startEnd = timesheet.start_time && timesheet.end_time
    ? moment(timesheet.start_time.replace("Z", "")).format("h:mm a") +
    " - " +
    moment(timesheet.end_time.replace("Z", "")).format("h:mm a")
    : "-";

  let dateOfService = "-";
  if (taskDets) {
    dateOfService = moment(taskDets.tas_end_time).isValid()
      ? moment(taskDets.tas_end_time).utc().format("MM/DD/YYYY")
      : moment(taskDets.tas_end_date).utc().format("MM/DD/YYYY h:mm a");
  }

  return {
    id: timesheet.id,
    Provider: empDets ? empDets.emp_first_name + " " + empDets.emp_last_name : "-",
    Receiver: timesheet.customer_name,
    TaskType: taskDets ? taskDets.tas_type : "-",
    Location: Array.isArray(locDets) ? locDets : [],
    StarttoEnd: startEnd,
    DateofService: dateOfService,
  };
};

const CELL_STYLE = "width:100%; border-bottom: 1px solid black";

const escapeHtml = (value) =>
  String(value).replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]));

// Opens a print window containing the exported rows (arrays ordered to match `columns`).
const printRows = (exportRows) => {
  const printWindow = window.open("");
  if (!printWindow) return;

  const header = columns
    .map((col) => `<th style='${CELL_STYLE}'>${escapeHtml(col.headerName)}</th>`)
    .join("");
  const body = exportRows
    .map((row) => `<tr>${row.map((cell) => `<td style='${CELL_STYLE}'>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");

  printWindow.document.write(`<table><tr>${header}</tr>${body}</table>`);
  printWindow.print();
  printWindow.close();
};

const StatusSnackbar = ({ severity, message, onClose }) => (
  <Snackbar open={message !== ""} autoHideDuration={30000} onClose={onClose}>
    <Alert sx={{ width: "100%" }} severity={severity} elevation={6} variant="filled">
      {message}
      <IconButton aria-label="close" color="inherit" sx={{ p: 0.25 }} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Alert>
  </Snackbar>
);

const EvvReport = React.forwardRef((props, ref) => {
  const { user, comp_id, customReportGridId } = props;
  const gridId = customReportGridId || DEFAULT_GRID_ID;

  const apiRef = useGridApiRef();
  useImperativeHandle(ref, () => apiRef.current);

  const [evvReports, setEvvReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [appliedRange, setAppliedRange] = useState(DEFAULT_DATE_RANGE);
  const [reportDetails, setReportDetails] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [errorText, setErrorText] = useState("");

  const fetchReportDetails = useCallback(async () => {
    try {
      const response = await axios.get(APIURL + "/jsreport/getAllReports", {
        params: { empID: user.emp_id },
      });
      const report = response?.data?.reports?.find((ele) => ele.report_name === REPORT_NAME);
      setReportDetails(report ?? {});
      setIsFavorite(report?.status === 1);
    } catch (error) {
      console.error("Failed to load report details", error);
    }
  }, [user.emp_id]);

  useEffect(() => {
    fetchReportDetails();
  }, [fetchReportDetails]);

  useEffect(() => {
    let isCurrent = true;
    setLoading(true);

    // Admins query the whole company, managers get both scopes, everyone else only their own rows.
    const timesheetParams = {
      employeeId: user.emp_id,
      companyId: comp_id,
      start: appliedRange[0]?.format("YYYY-MM-DD"),
      end: appliedRange[1]?.format("YYYY-MM-DD"),
      ...(user.type === "Admin" || user.type === "Manager" ? { comp_id } : {}),
      ...(user.type === "Admin" ? {} : { emp_id: user.emp_id }),
    };

    Promise.all([
      axios.get(APIURL + "/timesheets", { params: timesheetParams }),
      axios.get(APIURL + "/customers/v2/associated", {
        params: {
          company_id: comp_id,
          employee_id: user.emp_id,
          type: user.permissions.AdminLevelCustomers ? "Admin" : "Manager",
        },
      }),
    ])
      .then(([timesheetsResponse, customersResponse]) => {
        if (!isCurrent) return;

        const customers = customersResponse.data ?? [];
        const customerNameById = new Map(customers.map((cus) => [cus.cus_id, cus.cus_name]));

        const visits = (timesheetsResponse.data ?? [])
          .filter((timesheet) => /visit/i.test(timesheet.taskDets?.tas_type ?? ""))
          .map((timesheet) => ({
            ...timesheet,
            customer_name: customerNameById.get(timesheet.projDets?.pro_customer_id) ?? "",
          }));

        setEvvReports(visits);
      })
      .catch((error) => {
        if (!isCurrent) return;
        console.error("Failed to load EVV report", error);
        setErrorText("Unable to load the EVV report. Please try again.");
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [comp_id, user.emp_id, user.type, user.permissions.AdminLevelCustomers, appliedRange]);

  const markFavourite = async () => {
    const status = isFavorite ? 0 : 1;

    try {
      const response = await axios.put(APIURL + "/jsreport/markFavorite", {
        report_id: reportDetails?.report_id,
        status,
        empID: user.emp_id,
      });

      if (response?.status === 200 && response?.data?.code === 200) {
        setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
      } else {
        setErrorText("Something went wrong");
      }
    } catch (error) {
      setErrorText("Something went wrong");
    }

    fetchReportDetails();
  };

  const rows = useMemo(() => evvReports.map(buildRow), [evvReports]);

  // Positional rows shared by the CSV download and the print view.
  const exportRows = useMemo(
    () => rows.map((row) => columns.map((col) => (col.csvValue ? col.csvValue(row[col.field]) : row[col.field]))),
    [rows]
  );

  // CSVDownloader serializes this on click, so no CSV is built until the user asks for one.
  const csvData = useMemo(
    () => ({ fields: columns.map((col) => col.csv_header), data: exportRows }),
    [exportRows]
  );

  return (
    <div style={{ margin: 0, width: '100%', height: '100%', padding: '8px 10px 10px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', marginBottom: 10 }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{ start: "From Date", end: "To Date" }}
          >
            <DateRangePicker
              value={dateRange}
              onChange={(newValue) => setDateRange(newValue)}
              slotProps={{ fieldSeparator: { children: 'to' }, textField: { size: 'small' } }}
            />
          </LocalizationProvider>

          <Button
            onClick={() => setAppliedRange([...dateRange])}
            variant="contained"
            sx={{ marginLeft: 1 }}
          >
            SEARCH
          </Button>
        </div>
        <div>
          <CSVDownloader data={csvData} filename={"EVV report"} bom={true}>
            <button type="button" className="btn btn-sm btn-outline-info m-1">
              CSV
            </button>
          </CSVDownloader>

          <button
            type="button"
            className="btn btn-sm btn-outline-info m-1"
            onClick={() => printRows(exportRows)}
          >
            PDF
          </button>
          <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
            <img
              src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
              alt="barchart"
              style={{
                fontFamily: "Lato",
                width: "1.5rem",
                height: "1.5rem",
                cursor: "pointer",
                margin: 5
              }}
              onClick={markFavourite}
            />
          </Tooltip>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ControlledDataGrid
          gridId={gridId}
          user={user}
          initialState={{ sorting: { sortModel: [{ field: "DateofService", sort: "desc" }] } }}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : ''
          }
          sx={{
            '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#EAF4FC',
            },
            '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#DCEFFC',
            },
          }}
          loading={loading}
        />
      </div>
      <StatusSnackbar severity="success" message={successText} onClose={() => setSuccessText("")} />
      <StatusSnackbar severity="error" message={errorText} onClose={() => setErrorText("")} />
    </div>
  );
});

export default EvvReport;
